import React from "react";
import { createRoot } from "react-dom/client";
import { AuthProvider } from "react-oidc-context";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { ColorModeProvider } from "./theme/ColorModeProvider";
import { ScopeProvider } from "./auth/scopeContext";
import { loadConfig } from "./config";
import { oidcSettings, onSigninCallback } from "./auth/oidc";
import App from "./App";
import { FatalError } from "./components/FatalError";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (count, error) => {
        // Never retry an authorization decision — it will not change, and
        // retrying a 403 turns one clear refusal into four confusing ones.
        const kind = (error as { kind?: string })?.kind;
        if (kind === "unauthenticated" || kind === "forbidden" || kind === "fail-closed") return false;
        return count < 2;
      },
      staleTime: 30_000,
    },
  },
});

const root = createRoot(document.getElementById("root")!);

// Configuration is loaded BEFORE React mounts. Nothing in the tree may render
// against a half-known configuration, and a missing client id must stop the
// app rather than fall back to something that happens to work.
loadConfig()
  .then(() => {
    root.render(
      <React.StrictMode>
        <ColorModeProvider>
          <AuthProvider {...oidcSettings()} onSigninCallback={onSigninCallback}>
            <QueryClientProvider client={queryClient}>
              <BrowserRouter>
                <ScopeProvider>
                  <App />
                </ScopeProvider>
              </BrowserRouter>
            </QueryClientProvider>
          </AuthProvider>
        </ColorModeProvider>
      </React.StrictMode>,
    );
  })
  .catch((err: unknown) => {
    root.render(<FatalError title="AnkhOS could not start" detail={String(err)} />);
  });
